// usePageViewTracker — fires a single POST to /api/track/pageview per pathname.
// Mounted once near the top of App.js so every route change (and full load)
// records a view. Idempotent per-pathname in the same browser session.
import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function usePageViewTracker() {
  const lastPath = useRef(null);

  useEffect(() => {
    const fire = () => {
      const path = typeof window !== 'undefined' ? window.location.pathname : '/';
      if (lastPath.current === path) return;
      lastPath.current = path;
      try {
        const body = JSON.stringify({
          path,
          referrer: (typeof document !== 'undefined' && document.referrer) || null,
        });
        // Prefer sendBeacon so we don't block unload / navigation.
        if (navigator?.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(`${API_URL}/api/track/pageview`, blob);
        } else {
          fetch(`${API_URL}/api/track/pageview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => { /* best-effort, silent */ });
        }
      } catch {
        /* best-effort, silent */
      }
    };

    // Initial mount
    fire();

    // React to history navigation within the SPA. App.js uses path-based
    // rendering (no react-router), so we listen on popstate + a light
    // polling fallback for manual pushState calls from other code paths.
    const onPop = () => fire();
    window.addEventListener('popstate', onPop);
    const intv = setInterval(fire, 3000);  // catches pushState route swaps

    return () => {
      window.removeEventListener('popstate', onPop);
      clearInterval(intv);
    };
  }, []);
}
